// App.tsx — now lean: only providers + global side-effects
import { RouterProvider } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useDispatch, useSelector } from "./redux/hooks";
import { Suspense, useEffect, useMemo } from "react";
import { setDarkMode } from "./redux/themeSlice";
import Cookies from "universal-cookie";
import store from "./redux/store";
import {
  logout,
  setOfflineStatus,
  setToken,
} from "./redux/authSlice";
import { jwtDecode } from "jwt-decode";
import { CookiesToken } from "./models/auth";
import { CookiesProvider } from "./contexts/cookiesContext";
import { RootState } from "@/redux/store";
import PageLoader from "./components/PageLoader";
import { syncWithLocalStorage } from "./redux/userTypeSlice";
import OfflineToast from "./components/OfflineToast";
import SessionManager from "./components/SessionManager";
import AutoReloadChecker from "./components/AutoReloadChecker";
import Chatbot from "./components/Chatbot/Chatbot";
import { router } from "./components/appLayouts/router";
import DevelopmentBanner from "./components/DevelopmentBanner";

const IN_DEVELOPMENT =
  import.meta.env.VITE_IN_DEVELOPMENT === "true" ||
  import.meta.env.VITE_IN_DEVELOPMENT === true;

// ✅ Also module-level — never recreated
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<CookiesToken>(token);
    return decoded.exp * 1000 < Date.now();
  } catch (error) {
    console.error("Invalid token:", error);
    return true;
  }
};

function App() {
  const { darkMode } = useSelector(
    (state: RootState) => state.darkMode
  );
  const dispatch = useDispatch();

  // ── Dark mode ──────────────────────────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem("darkMode");
    if (saved) dispatch(setDarkMode(JSON.parse(saved)));
    dispatch(syncWithLocalStorage());
  }, [dispatch]);

  // ── Online / offline status ────────────────────────────────
  useEffect(() => {
    dispatch(setOfflineStatus(!navigator.onLine));

    const handleOnline = () => dispatch(setOfflineStatus(false));
    const handleOffline = () => dispatch(setOfflineStatus(true));

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [dispatch]);

  // ── Token hydration ────────────────────────────────────────
  const cookies = useMemo(() => new Cookies(), []);

  useEffect(() => {
    const tokenFromStore = store.getState().auth.token;
    const cookieToken = cookies.get("jwt_authorisation");

    if (!tokenFromStore && cookieToken) {
      dispatch(setToken(cookieToken));
    }

    const tokenToCheck = tokenFromStore || cookieToken;
    if (tokenToCheck && isTokenExpired(tokenToCheck)) {
      dispatch(logout());
    }
  }, [cookies, dispatch]);

  // ──────────────────────────────────────────────────────────
  if (IN_DEVELOPMENT) return <DevelopmentBanner />;

  return (
    <QueryClientProvider client={queryClient}>
      <CookiesProvider>
        {/* Global utilities rendered outside the router
            — they don't need router context */}
        <AutoReloadChecker />
        <SessionManager />
        <OfflineToast />
        <Chatbot />

        <Suspense fallback={<PageLoader />}>
          {/* ✅ router is stable — no remounts */}
          <RouterProvider router={router} />
        </Suspense>

        <Toaster
          position="top-center"
          gutter={12}
          toastOptions={{
            success: { duration: 3000 },
            error: { duration: 5000 },
            style: {
              background: darkMode ? "#334155" : "#f1f5f9",
              color: darkMode ? "#fff" : "#17171D",
              fontSize: "14px",
              maxWidth: "25rem",
              padding: "1rem",
            },
          }}
        />
      </CookiesProvider>
    </QueryClientProvider>
  );
}

export default App;